import { Box, Card, Typography } from '@mui/material';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import { StRedPacketWithdrawal } from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { StEmpty, StIdCell, StNameIdCell, StStatusChip, stFormatDateTime, stText } from '../StListUi';
import './init';

const TARGET_ACCOUNTS = ['PERP', 'PREDICT_SPOT', 'PREDICT_MARGIN', 'PREDICT_GRID'] as const;
const STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const;

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <SelectInput
        source="status"
        label={t('module.socialTrading.colStatus')}
        choices={STATUSES.map((id) => ({
          id,
          name: t(`module.socialTrading.withdrawalStatus.${id}`),
        }))}
        alwaysOn
        resettable
      />
      <TextInput source="userId" label={t('module.socialTrading.colUserId')} alwaysOn resettable />
      <SelectInput
        source="targetAccount"
        label={t('module.socialTrading.colTargetAccount')}
        choices={TARGET_ACCOUNTS.map((id) => ({
          id,
          name: t(`module.socialTrading.targetAccount.${id}`),
        }))}
        alwaysOn
        resettable
        sx={{ minWidth: 180 }}
      />
    </Filter>
  );
};

function targetLabel(t: (key: string) => string, account?: string | null): string {
  if (!account) return '—';
  const key = `module.socialTrading.targetAccount.${account}`;
  const label = t(key);
  return label === key ? account : label;
}

export default function SocialTradingRedpacketWithdrawalsList() {
  const t = useTranslate();

  return (
    <PermissionGuard resource="st-redpacket-withdrawals" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.redpacketWithdrawals')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.redpacketWithdrawals') },
          ]}
        />

        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={Resources.socialTradingRedpacketWithdrawals}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyWithdrawals')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colClaimId')}
                  render={(row: StRedPacketWithdrawal) => <StIdCell value={row.id} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colUser')}
                  render={(row: StRedPacketWithdrawal) => (
                    <StNameIdCell name={row.nickname} id={row.userId} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colAmount')}
                  render={(row: StRedPacketWithdrawal) => stText(row.amount)}
                />
                <FunctionField
                  label={t('module.socialTrading.colCurrency')}
                  render={(row: StRedPacketWithdrawal) => stText(row.currency)}
                />
                <FunctionField
                  source="targetAccount"
                  label={t('module.socialTrading.colTargetAccount')}
                  sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
                  render={(row: StRedPacketWithdrawal) => stText(targetLabel(t, row.targetAccount))}
                />
                <FunctionField
                  label={t('module.socialTrading.colStatus')}
                  render={(row: StRedPacketWithdrawal) => <StStatusChip status={row.status} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colAppliedAt')}
                  render={(row: StRedPacketWithdrawal) => stText(stFormatDateTime(row.createdAt))}
                />
                <FunctionField
                  label={t('module.socialTrading.colCompletedAt')}
                  render={(row: StRedPacketWithdrawal) =>
                    row.completedAt ? stText(stFormatDateTime(row.completedAt)) : <StEmpty />
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colTxHash')}
                  render={(row: StRedPacketWithdrawal) =>
                    row.txHash ? <StIdCell value={row.txHash} head={10} tail={8} /> : <StEmpty />
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colFailureReason')}
                  render={(row: StRedPacketWithdrawal) => stText(row.failureReason)}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
